import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  PopoverBody,
  PopoverContent,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { faCheck, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  ChakraStylesConfig,
  MultiValue,
  Select,
  SingleValue,
} from 'chakra-react-select';
import { debounce, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@unleash/proxy-client-react';
import { ReactComponent as SparkelCopilotIcon } from '../../../assets/icons/sparkel-copilot.svg';
import { getPredicateLabels } from '../../../domain/dbid-predicates';
import { displayNameFromProperty } from '../../../domain/friendly-properties';
import {
  Point,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from '../../../domain/geometry/geometric-types';
import {
  PropertyContext,
  resolveProperties,
  shapesPropertySet,
} from '../../../domain/property-operations';
import {
  ForgeAttributeQueryType,
  PredicateType,
  PropertySetType,
  UnitSettingsType,
} from '../../../gql/graphql';
import { PropertyCascader } from '../../common/PropertyCascader';
import SparkelIcon from '../../common/icon/SparkelIcon';
import { AiPromptLinkingPanel } from './AiPromptLinkingPanel';
import { isRegexValid } from 'src/utils/regex-valid';

type DynamicDbidsPopoverProps = {
  propertySets: PropertySetType[];
  isOpen: boolean;
  isUpdatingTable: boolean;
  defaultQuery?: ForgeAttributeQueryType | null;
  defaultUsePromptForLinking?: boolean;
  defaultPromptForLinking?: string;
  onLink: (query: ForgeAttributeQueryType) => Promise<void> | void;
  onUnlink: () => Promise<void> | void;
  onClearPromptLinking?: () => Promise<void> | void;
  onUsingCascader?: (e: boolean) => void;
  propertyContext: PropertyContext;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
    manuallyApplyFilters?: boolean;
  };
  onApplyPromptLinking?: (input: {
    prompt: string;
    usePromptForLinking: boolean;
  }) => Promise<void> | void;
};

type CascaderData = {
  label: string;
  children?: CascaderData[];
};

type DynamicDbidsPopoverFormData = {
  filters: {
    selectedDisplayCategory: string | null;
    selectedAttributeName: string | null;
    selectedPredicate: PredicateType | null;
    predicateValues: string[];
  }[];
};

const emptyQuery = {
  selectedDisplayCategory: null,
  selectedAttributeName: null,
  selectedPredicate: null,
  predicateValues: [],
};

// Function is placed outside of component to prevent re-creation on every render.
// useCallback didn't like being used together with debounce.
const debouncedInputValue = debounce(
  (setValue, idx, value, validateFilters) => {
    setValue(`filters.${idx}.predicateValues.0`, value);
    validateFilters();
  },
  2000
);

// eslint-disable-next-line complexity
export function DynamicDbidsPopover({
  propertySets,
  isOpen,
  defaultQuery,
  isUpdatingTable,
  defaultUsePromptForLinking,
  defaultPromptForLinking,
  onLink: onDynamicDbids,
  onUnlink,
  onClearPromptLinking,
  onUsingCascader,
  propertyContext,
  projectSettings,
  onApplyPromptLinking,
}: DynamicDbidsPopoverProps): React.ReactElement {
  const { control, setValue, getValues } = useForm<DynamicDbidsPopoverFormData>(
    { defaultValues: { filters: [] } }
  );
  const output = useWatch({
    name: 'filters',
    control,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'filters',
  });

  const enablePromptLinking = useFlag('enable-prompt-linking');

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSubmittingLink, setSubmittingLink] = useState(false);
  const [isSubmittingUnlink, setSubmittingUnlink] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUpdatedTable, setHasUpdatedTable] = useState(false);
  const [anyOfOptions, setAnyOfOptions] = useState(
    new Map<string, Map<string, (string | number)[]>>()
  );
  const [activeFilters, setActiveFilters] = useState<
    {
      selectedDisplayCategory: string | null;
      selectedAttributeName: string | null;
      selectedPredicate: PredicateType | null;
      predicateValues: string[];
    }[]
  >([]);
  const [activeFilterIndices, setActiveFilterIndices] = useState<number[]>([]);
  const [lastSubmittedFilters, setLastSubmittedFilters] = useState<
    {
      selectedDisplayCategory: string | null;
      selectedAttributeName: string | null;
      selectedPredicate: PredicateType | null;
      predicateValues: string[];
    }[]
  >([]);

  const [activeInputValues, setActiveInputValues] = useState<string[]>([]);
  const [activeAnyOfOptions, setActiveAnyOfOptions] = useState<string[][]>([]);

  // AI prompt linking state
  const [isAiMode, setIsAiMode] = useState(false);

  const { t } = useTranslation(['common', 'categories', 'table']);

  const handleInputChange = (value: string, idx: number, debounce: boolean) => {
    // Update the input value in the input values array
    setActiveInputValues((prev) => {
      const newInputValues = [...prev];
      newInputValues[idx] = value;
      return newInputValues;
    });

    if (debounce) {
      debouncedInputValue(setValue, idx, value, validateFilters);
      return;
    }

    setValue(`filters.${idx}.predicateValues`, [value]);
    validateFilters();
  };

  const debouncedAnyOfValueRef = useRef(new Map());

  const getDebouncedUpdate = (selectId: number) => {
    if (!debouncedAnyOfValueRef.current.has(selectId)) {
      debouncedAnyOfValueRef.current.set(
        selectId,
        debounce((setValue, idx, values, validateFilters) => {
          setValue(`filters.${idx}.predicateValues`, values);
          validateFilters();
        }, 1000)
      );
    }
    return debouncedAnyOfValueRef.current.get(selectId);
  };

  const handleAnyOfChange = (values: string[], idx: number) => {
    setActiveAnyOfOptions((prev) => {
      const newActiveAnyOfOptions = [...prev];
      newActiveAnyOfOptions[idx] = values;
      return newActiveAnyOfOptions;
    });
    const debouncedUpdate = getDebouncedUpdate(idx);
    debouncedUpdate(setValue, idx, values, validateFilters);
  };

  // Hook for updating loading state
  useEffect(() => {
    if (!isSubmittingLink && !isSubmittingUnlink && hasUpdatedTable) {
      setIsLoading(false);
      setHasUpdatedTable(false);
    }
  }, [isSubmittingLink, isSubmittingUnlink, hasUpdatedTable]);

  // Hook for updating active filters
  useEffect(() => {
    setActiveFilters(
      activeFilterIndices.map((index) => ({
        selectedDisplayCategory: output[index]?.selectedDisplayCategory,
        selectedAttributeName: output[index]?.selectedAttributeName,
        selectedPredicate: output[index]?.selectedPredicate,
        predicateValues: output[index]?.predicateValues,
      }))
    );
  }, [activeFilterIndices, output]);

  const validateFilters = () => {
    const activeFilterIndices: number[] = [];
    const updatedInputValues: string[] = [];
    const updatedAnyOfOptions: string[][] = [];

    // eslint-disable-next-line complexity
    fields.forEach((_, index) => {
      const selectedDisplayCategory = getValues(
        `filters.${index}.selectedDisplayCategory`
      );
      const selectedAttributeName = getValues(
        `filters.${index}.selectedAttributeName`
      );
      const selectedPredicate = getValues(`filters.${index}.selectedPredicate`);
      const selectedPredicateValues = getValues(
        `filters.${index}.predicateValues`
      );

      const hasSelectedDisplayCategory = !!selectedDisplayCategory;
      const hasSelectedAttributeName = !!selectedAttributeName;
      const hasSelectedPredicate = !!selectedPredicate;
      const validRegex =
        selectedPredicate !== PredicateType.FromRegex ||
        isRegexValid(selectedPredicateValues[0] as string);
      const hasValidPredicateValues =
        selectedPredicate === PredicateType.Exists ||
        selectedPredicate === PredicateType.NotExists ||
        (!!selectedPredicateValues &&
          selectedPredicateValues.length > 0 &&
          selectedPredicateValues[0] !== '' &&
          validRegex);

      if (
        hasSelectedDisplayCategory &&
        hasSelectedAttributeName &&
        hasSelectedPredicate &&
        hasValidPredicateValues
      ) {
        activeFilterIndices.push(index);
      }

      if (
        selectedPredicate === PredicateType.Contains ||
        selectedPredicate === PredicateType.NotContains ||
        selectedPredicate === PredicateType.GreaterThan ||
        selectedPredicate === PredicateType.LessThan ||
        selectedPredicate === PredicateType.FromRegex
      ) {
        // These predicates uses an input
        updatedInputValues.push(selectedPredicateValues[0] || '');
        updatedAnyOfOptions.push([]);
      } else if (selectedPredicate === PredicateType.AnyOf) {
        // AnyOf uses a multi-select
        updatedInputValues.push('');
        updatedAnyOfOptions.push(selectedPredicateValues);
      } else {
        // All other predicates use a single select
        updatedInputValues.push('');
        updatedAnyOfOptions.push([]);
      }
    });

    setActiveInputValues(updatedInputValues);
    setActiveAnyOfOptions(updatedAnyOfOptions);
    setActiveFilterIndices(activeFilterIndices);
  };

  useEffect(() => {
    if (isOpen) {
      if (!isInitialLoad) {
        return;
      }
      // Activate AI mode by default if selected entries all use prompt linking
      if (defaultUsePromptForLinking) {
        setIsAiMode(true);
      }
      if (defaultQuery?.predicates && defaultQuery.predicates.length > 0) {
        const defaultValues = defaultQuery.predicates.map((predicate) => ({
          selectedDisplayCategory: predicate.attribute.category,
          selectedAttributeName: predicate.attribute.name,
          selectedPredicate: predicate.predicate,
          predicateValues: predicate.attributeValues ?? [],
        }));
        replace(defaultValues);

        // Set default values for cascader
        const defaultActiveIndices: number[] = [];
        const defaultInputValues: string[] = [];
        const defaultAnyOfOptions: string[][] = [];

        defaultValues.forEach((value, index) => {
          const { selectedDisplayCategory, selectedAttributeName } = value;
          if (selectedDisplayCategory && selectedAttributeName) {
            defaultActiveIndices.push(index);
          }

          // Set the input values if the predicate is contains, does not contain, greater than or less than
          if (
            value.selectedPredicate === PredicateType.Contains ||
            value.selectedPredicate === PredicateType.NotContains ||
            value.selectedPredicate === PredicateType.GreaterThan ||
            value.selectedPredicate === PredicateType.LessThan ||
            value.selectedPredicate === PredicateType.FromRegex
          ) {
            defaultInputValues.push(value.predicateValues[0]);
            defaultAnyOfOptions.push([]);
          } else if (value.selectedPredicate === PredicateType.AnyOf) {
            defaultInputValues.push('');
            defaultAnyOfOptions.push(value.predicateValues);
          } else {
            defaultInputValues.push('');
            defaultAnyOfOptions.push([]);
          }
        });

        setActiveInputValues(defaultInputValues);
        setActiveAnyOfOptions(defaultAnyOfOptions);
        setActiveFilterIndices(defaultActiveIndices);
        setActiveFilters(defaultValues);
        setLastSubmittedFilters(defaultValues);
      } else {
        replace(emptyQuery);
        setActiveFilterIndices([]);
        setActiveFilters([]);
        setLastSubmittedFilters([]);
      }

      Promise.resolve().then(() => setIsInitialLoad(false));
    } else {
      setIsInitialLoad(true);
    }
  }, [
    replace,
    defaultQuery,
    isOpen,
    isInitialLoad,
    defaultUsePromptForLinking,
  ]);

  const submitForm = useCallback(
    async (
      filters: {
        selectedDisplayCategory: string | null;
        selectedAttributeName: string | null;
        selectedPredicate: PredicateType | null;
        predicateValues: string[];
      }[]
    ) => {
      const query: ForgeAttributeQueryType = {
        predicates: filters.map((filter) => {
          // NOTE: Use a yup schema and proper validation messages and isRequired here
          invariant(
            filter.selectedDisplayCategory,
            'Select a category to submit'
          );
          invariant(
            filter.selectedAttributeName,
            'Select an attribute to submit'
          );
          invariant(filter.selectedPredicate, 'Select a predicate to submit');
          return {
            predicate: filter.selectedPredicate,
            attribute: {
              name: filter.selectedAttributeName,
              category: filter.selectedDisplayCategory,
            },
            attributeValues: filter.predicateValues ?? [''],
          };
        }),
      };
      setIsLoading(true);
      try {
        await onDynamicDbids(query);
      } catch (error) {
        setIsLoading(false);
        return Promise.reject(error);
      }
    },
    [onDynamicDbids]
  );

  // Hook for determining if changes have been made
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }
    if (activeFilters.length !== lastSubmittedFilters.length) {
      setChangesMade(true);
      return;
    }

    setChangesMade(
      JSON.stringify(activeFilters) !== JSON.stringify(lastSubmittedFilters)
    );
  }, [activeFilters, isInitialLoad, lastSubmittedFilters]);

  const applyFilters = useCallback(() => {
    setChangesMade(false);

    if (activeFilters.length === 0) {
      // Unlink if there are no active filters
      setSubmittingUnlink(true);

      (onUnlink() as Promise<void>).finally(() => {
        setSubmittingUnlink(false);
      });

      setLastSubmittedFilters([]);
      return;
    }

    setSubmittingLink(true);

    // Map quantities to geometric property set when submitting
    submitForm(
      activeFilters.map((filter) => {
        if (
          filter.selectedDisplayCategory === t('quantities') &&
          filter.selectedAttributeName
        ) {
          return {
            ...filter,
            selectedDisplayCategory: SPARKEL_GEOMETRIC_PROPERTY_SET,
            selectedAttributeName: displayNameFromProperty(
              filter.selectedAttributeName,
              t
            ),
          };
        } else if (
          filter.selectedDisplayCategory === t('location') &&
          filter.selectedAttributeName
        ) {
          return {
            ...filter,
            selectedDisplayCategory: SPARKEL_LOCATION_PROPERTY_SET,
            selectedAttributeName: displayNameFromProperty(
              filter.selectedAttributeName,
              t
            ),
          };
        }
        return filter;
      })
    )
      .then(() => {
        setLastSubmittedFilters(activeFilters);
      })
      .finally(() => {
        setSubmittingLink(false);
      });
  }, [activeFilters, submitForm, onUnlink, t]);

  // Submit linking when active filters change
  useEffect(() => {
    if (
      changesMade &&
      !isInitialLoad &&
      !projectSettings.manuallyApplyFilters
    ) {
      if (
        JSON.stringify(activeFilters) !== JSON.stringify(lastSubmittedFilters)
      ) {
        applyFilters();
      }
    }
  }, [
    activeFilters,
    applyFilters,
    changesMade,
    isInitialLoad,
    lastSubmittedFilters,
    projectSettings.manuallyApplyFilters,
    onUnlink,
    submitForm,
    t,
  ]);

  const onApplyFilters = useCallback(() => {
    applyFilters();
  }, [applyFilters]);

  const propertiesByCategory: { [category: string]: string[] } = useMemo(
    () =>
      Object.fromEntries(
        propertySets.map((set) => [set.category, set.properties])
      ),
    [propertySets]
  );

  const categoryOptions = useMemo(
    () =>
      Object.keys(propertiesByCategory).map((key) => ({
        label: key,
        options: propertiesByCategory[key].map((attribute) => ({
          label: attribute,
        })),
      })),

    // TODO: Add other categories
    /*[
      { label: 'Information', options: FRIENDLY_ATTRIBUTE_OPTIONS },
      {
        label: 'Quantities',
        options: [{ label: 'Coming soon', value: 'Coming soon' }],
      },
      {
        label: 'BIM',
        options: Object.keys(propertiesByCategory).map((key) => ({
          label: key,
          value: key,
        })),
      },
      {
        label: 'Calculations',
        options: [{ label: 'Coming soon', value: 'Coming soon' }],
      },
    ]*/ [propertiesByCategory]
  );

  const populateAnyOfOptions = async (index: number) => {
    const attributeName = output[index]?.selectedAttributeName;
    const category = output[index]?.selectedDisplayCategory;
    if (!attributeName || !category) {
      return;
    }
    const attributeNameToValues = anyOfOptions.get(category);
    const currentOptions = attributeNameToValues?.get(attributeName);
    //Shape property values are not static
    if (!currentOptions || category === shapesPropertySet) {
      let options = (
        await resolveProperties(propertyContext, projectSettings, {
          category,
          name: attributeName,
        })
      ).map((property) => property.value);
      options = uniq(options);
      // If options are 0 and 1, display them as Yes and No
      if (options.length === 2 && options[0] === 0 && options[1] === 1) {
        options[0] = 'No';
        options[1] = 'Yes';
      }
      setAnyOfOptions(
        new Map(
          anyOfOptions.set(
            category,
            attributeNameToValues?.set(attributeName, options) ??
              new Map().set(attributeName, options)
          )
        )
      );
    }
  };

  // Initialize the cascader data
  const propertiesforCascader: CascaderData[] = useMemo(() => {
    return categoryOptions.map((set) => {
      let label = set.label;
      if (set.label === SPARKEL_GEOMETRIC_PROPERTY_SET) {
        label = t('quantities');
      } else if (set.label === SPARKEL_LOCATION_PROPERTY_SET) {
        label = t('location');
      }
      return {
        label,
        children:
          set.options &&
          set.options.map((option) => ({
            label:
              set.label === SPARKEL_GEOMETRIC_PROPERTY_SET ||
              set.label === SPARKEL_LOCATION_PROPERTY_SET
                ? displayNameFromProperty(option.label, t)
                : option.label,
          })),
      };
    });
  }, [categoryOptions, t]);

  // Callback for retrieving indices of based on display category and attribute name
  const getCascaderIndices = (
    displayCategory: string,
    attributeName: string
  ) => {
    const propertySetIndex = categoryOptions.findIndex(
      (pSet) => pSet.label === displayCategory
    );
    if (
      categoryOptions[propertySetIndex] &&
      categoryOptions[propertySetIndex].options
    ) {
      const propertyIndex = categoryOptions[propertySetIndex].options.findIndex(
        (property) => property.label === attributeName
      );
      return [propertySetIndex, propertyIndex];
    }
  };

  // Callback for when a property is selected in the cascader
  const onPropertySelect = (
    cascaderIndex: number,
    selectedIndices: number[]
  ) => {
    // The indices are hardcoded, so if we change the cascader data, we need to change this
    const selectedCategory = categoryOptions[selectedIndices[0]].label;
    const selectedAttribute =
      propertiesByCategory[selectedCategory][selectedIndices[1]];

    // Need to set values directly, since cascader is setting both values at once
    setValue(
      `filters.${cascaderIndex}.selectedDisplayCategory`,
      selectedCategory
    );
    setValue(
      `filters.${cascaderIndex}.selectedAttributeName`,
      selectedAttribute
    );

    // Reset predicate values when selecting a new property
    setValue(`filters.${cascaderIndex}.predicateValues`, []);
  };

  // Hook for making sure loading is set while table is updating
  useEffect(() => {
    if (isOpen) {
      if (isUpdatingTable) {
        setIsLoading(true);
      } else {
        setHasUpdatedTable(true);
      }
    }
  }, [isOpen, isUpdatingTable]);

  const getAnyOfOptions = (index: number) => {
    const displayCategory = output[index]?.selectedDisplayCategory;
    const attributeName = output[index]?.selectedAttributeName;
    if (displayCategory && attributeName) {
      return (
        anyOfOptions
          .get(displayCategory)
          ?.get(attributeName)
          ?.sort((a, b) => {
            if (typeof a === 'number' && typeof b === 'number') {
              // Both numbers: sort in ascending order
              return a - b;
            } else if (typeof a === 'string' && typeof b === 'string') {
              // Both strings: sort in alphabetical order
              return a.localeCompare(b);
            } else {
              // Number and string: number should come first
              return typeof a === 'number' ? 1 : -1;
            }
          })
          ?.map((e) => ({
            label: String(e),
            value: String(e),
          })) ?? []
      );
    }
    return [];
  };

  const dropdownValueColor = useColorModeValue('gray.800', 'gray.50');
  const activeDropdownValueColor = useColorModeValue('teal.500', 'teal.200');
  const dropdownIndicatorColor = useColorModeValue('gray.300', 'gray.500');
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const selectedItemTextColor = useColorModeValue('blue.700', 'blue.200');
  const selectedItemBackgroundColor = useColorModeValue(
    'blue.50',
    'rgba(66, 153, 225, 0.24)'
  );

  const chakraStyles: ChakraStylesConfig = {
    control: (provided, state) => {
      return {
        ...provided,
        borderRadius: 'base',
        minHeight: 6,
        maxHeight: 78,
        overflowY: 'auto',
        height: state.isMulti ? 'auto' : 6,
        cursor: 'pointer',
      };
    },
    valueContainer: (provided) => {
      return {
        ...provided,
        paddingLeft: 2,
        fontSize: 'xs',
        marginTop: -0.5,
        paddingRight: 0,
      };
    },
    input: (provided) => {
      return {
        ...provided,
        color: dropdownValueColor,
      };
    },
    multiValue: (provided) => {
      return {
        ...provided,
        fontSize: 'xs',
        paddingTop: 0.5,
        paddingBottom: 0,
        minHeight: 0,
        maxWidth: 200,
        minWidth: 'fit-content',
      };
    },
    multiValueRemove: (provided) => {
      return {
        ...provided,
        paddingY: 0,
      };
    },
    clearIndicator: (provided) => {
      return {
        ...provided,
        padding: 0,
        width: 4,
        height: 4,
        fontSize: '0.5rem',
        colorScheme: 'gray',
      };
    },
    dropdownIndicator: (provided) => {
      return {
        ...provided,
        marginX: 0,
        padding: 0,
        paddingBottom: 1,
        color: dropdownIndicatorColor,
        marginTop: '2px',
      };
    },
    noOptionsMessage: (provided) => {
      return {
        ...provided,
        fontSize: 'xs',
      };
    },
    menuList: (provided) => {
      return {
        ...provided,
        borderRadius: 'md',
        padding: 1,
      };
    },
    menu: (provided) => {
      return {
        ...provided,
        minWidth: 200,
      };
    },
    option: (provided, state) => {
      return {
        ...provided,
        fontSize: 'xs',
        borderRadius: 'lg',
        paddingY: 0,
        paddingLeft: 2,
        minHeight: 7,
        fontWeight: state.isSelected ? 'bold' : 'normal',
        color: state.isSelected ? selectedItemTextColor : undefined,
        backgroundColor: state.isSelected
          ? selectedItemBackgroundColor
          : undefined,
        _hover: {
          color: state.isSelected ? selectedItemTextColor : undefined,
          backgroundColor: state.isSelected
            ? selectedItemBackgroundColor
            : hoverBackgroundColor,
        },
      };
    },
  };

  const activeSelectChakraStyles: ChakraStylesConfig = {
    ...chakraStyles,
    valueContainer: (provided) => {
      return {
        ...provided,
        paddingLeft: 2,
        fontSize: 'xs',
        marginTop: -0.5,
        color: activeDropdownValueColor,
        paddingRight: 0,
      };
    },
  };

  return (
    <PopoverContent shadow={'lg'} minWidth={'3xs'} width={'fit-content'}>
      <PopoverBody>
        <HStack justify="space-between" align="center">
          <Heading as={'h3'} size={'xs'} marginY={1}>
            {t('table:dynamic-link.link-elements')}
          </Heading>
          {enablePromptLinking && (
            <Tooltip label={t('table:prompt-linking.tooltip')}>
              <Button
                size={'xs'}
                variant={'ghost'}
                colorScheme={'purple'}
                borderRadius={'md'}
                isActive={isAiMode}
                paddingTop={0.5}
                leftIcon={
                  <Box marginTop={-0.5}>
                    <SparkelIcon as={SparkelCopilotIcon} color="inherit" />
                  </Box>
                }
                alignItems={'center'}
                justifyContent={'center'}
                textAlign={'center'}
                onClick={async () => {
                  const next = !isAiMode;
                  setIsAiMode(next);
                  if (
                    !next &&
                    onClearPromptLinking &&
                    defaultUsePromptForLinking &&
                    !!defaultPromptForLinking
                  ) {
                    await onClearPromptLinking();
                  }
                }}
              >
                AI
              </Button>
            </Tooltip>
          )}
        </HStack>
        {!isAiMode && (
          <VStack gap={1}>
            <Text
              alignSelf={'flex-start'}
              fontSize={'xs'}
              marginLeft={1}
              marginTop={2}
              color="gray.500"
            >
              {t('table:dynamic-link.where')}
            </Text>
            {fields
              ? // eslint-disable-next-line complexity
                fields.map((field, idx) => {
                  const selectedDisplayCategory = getValues(
                    `filters.${idx}.selectedDisplayCategory`
                  );
                  const selectedAttributeName = getValues(
                    `filters.${idx}.selectedAttributeName`
                  );
                  const selectedPredicate = getValues(
                    `filters.${idx}.selectedPredicate`
                  );
                  const selectedPredicateValues = getValues(
                    `filters.${idx}.predicateValues`
                  );

                  const isActive = activeFilterIndices.includes(idx);

                  return (
                    <>
                      <HStack key={idx} align={'start'} width={'100%'}>
                        <Box maxWidth={200} width={'100%'}>
                          <Controller
                            control={control}
                            name={`filters.${idx}.selectedAttributeName`}
                            rules={{ required: 'Please select a property' }}
                            render={() => {
                              let placeholder = t(
                                'table:dynamic-link.assign.placeholder'
                              );
                              if (
                                selectedDisplayCategory &&
                                selectedAttributeName
                              ) {
                                let displayCategory = selectedDisplayCategory;
                                let displayName = selectedAttributeName;

                                if (
                                  selectedDisplayCategory ===
                                  SPARKEL_GEOMETRIC_PROPERTY_SET
                                ) {
                                  displayCategory = t('quantities');
                                  displayName = displayNameFromProperty(
                                    selectedAttributeName,
                                    t
                                  );
                                } else if (
                                  selectedDisplayCategory ===
                                  SPARKEL_LOCATION_PROPERTY_SET
                                ) {
                                  displayCategory = t('location');
                                  displayName = displayNameFromProperty(
                                    selectedAttributeName,
                                    t
                                  );
                                }

                                placeholder = `${displayCategory} / ${displayName}`;
                              }

                              return (
                                <PropertyCascader
                                  aria-label={`property cascader ${idx}`}
                                  data={propertiesforCascader}
                                  size="xs"
                                  variant={'filled'}
                                  isLoading={isLoading}
                                  closeOnSelect
                                  _placeholder={{
                                    color:
                                      selectedDisplayCategory &&
                                      selectedAttributeName
                                        ? isActive
                                          ? activeDropdownValueColor
                                          : dropdownValueColor
                                        : undefined,
                                  }}
                                  placeholder={placeholder}
                                  defaultSelection={
                                    selectedDisplayCategory &&
                                    selectedAttributeName
                                      ? getCascaderIndices(
                                          selectedDisplayCategory,
                                          selectedAttributeName
                                        )
                                      : undefined
                                  }
                                  onCascaderClose={() => {
                                    if (onUsingCascader && isOpen)
                                      onUsingCascader(false);
                                  }}
                                  onCascaderOpen={() => {
                                    if (onUsingCascader && isOpen)
                                      onUsingCascader(true);
                                  }}
                                  onPropertySelect={(indices) => {
                                    onPropertySelect(idx, indices);
                                    validateFilters();
                                    if (onUsingCascader && isOpen)
                                      onUsingCascader(false);
                                  }}
                                />
                              );
                            }}
                          />
                        </Box>
                        {selectedDisplayCategory && selectedAttributeName ? (
                          <Box minWidth={144}>
                            <Controller
                              control={control}
                              name={`filters.${idx}.selectedPredicate`}
                              render={({ field: { onChange } }) => (
                                <Select
                                  aria-label={`predicate select ${idx}`}
                                  menuPlacement="auto"
                                  isRequired
                                  menuPortalTarget={document.body}
                                  isDisabled={isLoading}
                                  onMenuOpen={() =>
                                    onUsingCascader && onUsingCascader(true)
                                  }
                                  onMenuClose={() =>
                                    onUsingCascader && onUsingCascader(false)
                                  }
                                  styles={{
                                    menuPortal: (provided) => ({
                                      ...provided,
                                      zIndex: 99,
                                    }),
                                  }}
                                  chakraStyles={
                                    isActive
                                      ? activeSelectChakraStyles
                                      : chakraStyles
                                  }
                                  useBasicStyles
                                  variant="filled"
                                  options={Array.from(
                                    getPredicateLabels(t)
                                  ).map(([key, value]) => ({
                                    label: value,
                                    value: key,
                                  }))}
                                  value={
                                    selectedPredicate
                                      ? {
                                          label:
                                            getPredicateLabels(t).get(
                                              selectedPredicate
                                            ),
                                          value: selectedPredicate,
                                        }
                                      : null
                                  }
                                  onChange={(selected) => {
                                    onChange(
                                      (selected as { value: string })?.value
                                    );
                                    validateFilters();
                                  }}
                                />
                              )}
                            />
                          </Box>
                        ) : undefined}

                        {selectedDisplayCategory &&
                        selectedAttributeName &&
                        selectedPredicate &&
                        selectedPredicate !== PredicateType.Exists &&
                        selectedPredicate !== PredicateType.NotExists ? (
                          <Box flex={1} maxWidth={240} minWidth={100}>
                            {selectedPredicate === PredicateType.AnyOf ? (
                              <Controller
                                control={control}
                                name={`filters.${idx}.predicateValues`}
                                render={() => (
                                  <Select
                                    aria-label={`predicate value select ${idx}`}
                                    menuPlacement="auto"
                                    isMulti
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: (provided) => ({
                                        ...provided,
                                        zIndex: 99,
                                      }),
                                    }}
                                    closeMenuOnSelect={false}
                                    chakraStyles={
                                      isActive
                                        ? activeSelectChakraStyles
                                        : chakraStyles
                                    }
                                    variant="filled"
                                    tagVariant={isActive ? 'subtle' : 'solid'}
                                    isDisabled={isLoading}
                                    colorScheme={isActive ? 'teal' : undefined}
                                    useBasicStyles
                                    options={getAnyOfOptions(idx)}
                                    onMenuOpen={() => {
                                      onUsingCascader && onUsingCascader(true);
                                      populateAnyOfOptions(idx);
                                    }}
                                    onMenuClose={() =>
                                      onUsingCascader && onUsingCascader(false)
                                    }
                                    onChange={(selected) => {
                                      const values = (
                                        selected as MultiValue<{
                                          value: string;
                                        }>
                                      )?.map((e) => e.value);
                                      handleAnyOfChange(values, idx);
                                    }}
                                    value={activeAnyOfOptions[idx].map((e) => ({
                                      label: e,
                                      value: e,
                                    }))}
                                  />
                                )}
                              />
                            ) : selectedPredicate === PredicateType.Equals ||
                              selectedPredicate === PredicateType.NotEquals ? (
                              <Controller
                                control={control}
                                name={`filters.${idx}.predicateValues`}
                                render={({ field: { onChange } }) => (
                                  <Select
                                    aria-label={`predicate value select ${idx}`}
                                    menuPlacement="auto"
                                    chakraStyles={
                                      isActive
                                        ? activeSelectChakraStyles
                                        : chakraStyles
                                    }
                                    variant="filled"
                                    tagVariant="solid"
                                    isDisabled={isLoading}
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: (provided) => ({
                                        ...provided,
                                        zIndex: 99,
                                      }),
                                    }}
                                    useBasicStyles
                                    options={getAnyOfOptions(idx)}
                                    onMenuOpen={() => {
                                      onUsingCascader && onUsingCascader(true);
                                      populateAnyOfOptions(idx);
                                    }}
                                    onMenuClose={() =>
                                      onUsingCascader && onUsingCascader(false)
                                    }
                                    onChange={(selected) => {
                                      onChange([
                                        (
                                          selected as SingleValue<{
                                            value: string;
                                          }>
                                        )?.value,
                                      ]);
                                      validateFilters();
                                    }}
                                    value={
                                      selectedPredicateValues &&
                                      selectedPredicateValues.length > 0
                                        ? selectedPredicateValues
                                            .filter((e) => e !== '')
                                            .map((e) => ({
                                              label: e,
                                              value: e,
                                            }))
                                        : [undefined]
                                    }
                                  />
                                )}
                              />
                            ) : (
                              <Input
                                aria-label={`predicate value input ${idx}`}
                                isRequired
                                isDisabled={isLoading}
                                size={'xs'}
                                height={6}
                                top={-0.5}
                                borderRadius={'base'}
                                variant={'filled'}
                                color={
                                  isActive
                                    ? activeDropdownValueColor
                                    : undefined
                                }
                                onChange={(event) =>
                                  handleInputChange(
                                    event.target.value,
                                    idx,
                                    true
                                  )
                                }
                                onBlur={(event) =>
                                  handleInputChange(
                                    event.target.value,
                                    idx,
                                    false
                                  )
                                }
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleInputChange(
                                      (event.target as HTMLInputElement).value,
                                      idx,
                                      false
                                    );
                                  }
                                }}
                                value={activeInputValues[idx] ?? ''}
                              />
                            )}
                          </Box>
                        ) : (
                          <Box flex={1} />
                        )}

                        {(activeFilterIndices.length !== 0 ||
                          selectedAttributeName) && (
                          <Tooltip
                            label={t(
                              'table:dynamic-link.remove-filter-tooltip'
                            )}
                          >
                            <IconButton
                              size={'xs'}
                              colorScheme={'gray'}
                              variant={'ghost'}
                              isLoading={isLoading}
                              aria-label="remove filter"
                              icon={
                                <SparkelIcon icon={faTimes} color={'inherit'} />
                              }
                              onClick={() => {
                                if (fields.length === 1) {
                                  append(emptyQuery);
                                }
                                remove(idx);
                                validateFilters();
                              }}
                            />
                          </Tooltip>
                        )}
                      </HStack>
                      {idx !== output.length - 1 ? (
                        <Text
                          alignSelf={'flex-start'}
                          fontSize={'xs'}
                          marginLeft={1}
                          marginTop={2}
                          color="gray.500"
                        >
                          {t('table:dynamic-link.and')}
                        </Text>
                      ) : undefined}
                    </>
                  );
                })
              : undefined}
          </VStack>
        )}
        {isAiMode && (
          <AiPromptLinkingPanel
            isOpen={isOpen}
            isUpdatingTable={isUpdatingTable}
            activePrompt={defaultPromptForLinking}
            appliedQuery={defaultQuery ?? null}
            onApplyPromptLinking={onApplyPromptLinking}
          />
        )}
        {!isAiMode && activeFilterIndices.length === fields.length && (
          <Button
            marginTop={2}
            leftIcon={<SparkelIcon icon={faPlus} color="inherit" />}
            size={'xs'}
            variant={'ghost'}
            onClick={() => {
              append(emptyQuery);
            }}
          >
            {t('table:dynamic-link.add-filter')}
          </Button>
        )}
        {!isAiMode && projectSettings.manuallyApplyFilters && (
          <Box justifySelf={'flex-end'}>
            <Button
              size={'xs'}
              variant={'ghost'}
              colorScheme={'cyan'}
              leftIcon={<SparkelIcon icon={faCheck} color="inherit" />}
              onClick={onApplyFilters}
              isLoading={isSubmittingLink || isSubmittingUnlink}
              isDisabled={
                JSON.stringify(activeFilters) ===
                JSON.stringify(lastSubmittedFilters)
              }
            >
              {t('table:dynamic-link.apply')}
            </Button>
          </Box>
        )}
      </PopoverBody>
    </PopoverContent>
  );
}
